import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { LocalDataProvider } from "./hooks/useLocalData";
import { ROLES } from "./config/roleConfig";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboard from "./pages/AdminDashboard";
import ClinicianDashboard from "./pages/ClinicianDashboard";
import FrontDeskDashboard from "./pages/FrontDeskDashboard";
import BillingDashboard from "./pages/BillingDashboard";
import CaseloadPage from "./pages/CaseloadPage";
import ActiveSessionPage from "./pages/ActiveSessionPage";
import ProcessingPage from "./pages/ProcessingPage";
import ReviewNotePage from "./pages/ReviewNotePage";
import NotesPage from "./pages/NotesPage";
import SettingsPage from "./pages/SettingsPage";
import SubmittedPage from "./pages/SubmittedPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import TrashPage from "./pages/TrashPage";
import OnboardingChat from "./pages/OnboardingChat";
import IndividualProfile from "./pages/IndividualProfile";
import SchedulePage from "./pages/SchedulePage";
import IntakePage from "./pages/IntakePage";
import ReferralPage from "./pages/ReferralPage";
import DischargePage from "./pages/DischargePage";
import RecoveryPage from "./pages/RecoveryPage";

// ─── Loading Spinner ──────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-surface-primary flex items-center justify-center">
      <div
        className="w-12 h-12 rounded-full"
        style={{
          border: "3px solid rgba(99,179,237,0.1)",
          borderTopColor: "#63b3ed",
          animation: "spin 1s linear infinite",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Protected Route (any authenticated user) ─────────────────────
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return children;
}

// ─── Role-Based Route (replaces old AdminRoute) ───────────────────
function RoleRoute({ children, allowed = [] }) {
  const { isAuthenticated, loading, role, homeRoute } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowed.length > 0 && !allowed.includes(role)) {
    return <Navigate to={homeRoute} replace />;
  }

  return children;
}

// ─── Responsive Shell ─────────────────────────────────────────────
function ResponsiveShell({ children }) {
  return (
    <div className="min-h-screen bg-surface-primary">
      <style>{`
        .app-shell { max-width: 100%; margin: 0 auto; position: relative; }
        @media (max-width: 768px) { .app-shell { max-width: 512px; } }
      `}</style>
      <div className="app-shell">
        {children}
      </div>
    </div>
  );
}

// ─── Smart Dashboard Router ──────────────────────────────────────
// Routes to the correct dashboard based on role
// CRITICAL: Must wait for profile to load, otherwise defaults to agent
function SmartDashboard() {
  const { role, profile, loading } = useAuth();

  // Wait for profile to actually load from Firestore
  // Without this, role defaults to "agent" on first render
  if (loading || !profile) return <LoadingScreen />;

  console.log("[SmartDashboard] Routing for role:", role);

  switch (role) {
    case ROLES.CLINICIAN:
    case ROLES.PRESCRIBER:
      return <ClinicianDashboard />;
    case ROLES.FRONTDESK:
      return <FrontDeskDashboard />;
    case ROLES.BILLING:
      return <BillingDashboard />;
    default:
      // Agent, supervisor, admin use DashboardPage
      return <DashboardPage />;
  }
}

// ─── Routes ───────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Admin/Supervisor Dashboard */}
      <Route path="/admin" element={
        <RoleRoute allowed={[ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <AdminDashboard />
        </RoleRoute>
      } />

      {/* Clinician Dashboard (direct access) */}
      <Route path="/clinician" element={
        <RoleRoute allowed={[ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ClinicianDashboard /></ResponsiveShell>
        </RoleRoute>
      } />

      {/* Front Desk Dashboard */}
      <Route path="/frontdesk" element={
        <RoleRoute allowed={[ROLES.FRONTDESK, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><FrontDeskDashboard /></ResponsiveShell>
        </RoleRoute>
      } />

      {/* Billing Dashboard */}
      <Route path="/billing-dashboard" element={
        <RoleRoute allowed={[ROLES.BILLING, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><BillingDashboard /></ResponsiveShell>
        </RoleRoute>
      } />

      {/* Smart Dashboard — routes to correct dashboard by role */}
      <Route path="/dashboard" element={
        <ProtectedRoute><ResponsiveShell><SmartDashboard /></ResponsiveShell></ProtectedRoute>
      } />

      {/* Common pages */}
      <Route path="/caseload" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><CaseloadPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute><ResponsiveShell><OnboardingChat /></ResponsiveShell></ProtectedRoute>
      } />
      <Route path="/individual/:individualId" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.FRONTDESK, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><IndividualProfile /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/analytics" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><AnalyticsPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/schedule" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.FRONTDESK, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><SchedulePage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/intake" element={
        <RoleRoute allowed={[ROLES.FRONTDESK, ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><IntakePage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/referrals" element={
        <RoleRoute allowed={[ROLES.FRONTDESK, ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ReferralPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/discharge/:individualId" element={
        <RoleRoute allowed={[ROLES.CLINICIAN, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><DischargePage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/notes" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.BILLING, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><NotesPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><ResponsiveShell><SettingsPage /></ResponsiveShell></ProtectedRoute>
      } />
      <Route path="/trash" element={
        <ProtectedRoute><ResponsiveShell><TrashPage /></ResponsiveShell></ProtectedRoute>
      } />
      <Route path="/recovery" element={
        <ProtectedRoute><ResponsiveShell><RecoveryPage /></ResponsiveShell></ProtectedRoute>
      } />

      {/* Session routes */}
      <Route path="/session/new" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ActiveSessionPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/session/new/continue" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ActiveSessionPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/session/new/:individualId" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ActiveSessionPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/session/active/:sessionId" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ActiveSessionPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/session/processing" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ProcessingPage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/session/review" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><ReviewNotePage /></ResponsiveShell>
        </RoleRoute>
      } />
      <Route path="/session/submitted" element={
        <RoleRoute allowed={[ROLES.AGENT, ROLES.CLINICIAN, ROLES.PRESCRIBER, ROLES.SUPERVISOR, ROLES.ADMIN]}>
          <ResponsiveShell><SubmittedPage /></ResponsiveShell>
        </RoleRoute>
      } />

      {/* Default redirects */}
      <Route path="/" element={<SmartHomeRedirect />} />
      <Route path="*" element={<SmartHomeRedirect />} />
    </Routes>
  );
}

// ─── Smart Home Redirect ──────────────────────────────────────────
function SmartHomeRedirect() {
  const { isAuthenticated, loading, homeRoute } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={homeRoute} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <LocalDataProvider>
        <Router>
          <AppRoutes />
        </Router>
      </LocalDataProvider>
    </AuthProvider>
  );
}